import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shadcn-ui/utils';
import Spinner from '@/icons/spinner';

const buttonVariants = cva('', {
  variants: {
    variant: {
      primary: '',
      secondary: '',
      tertiary: '',
      destructive: '',
      clear: '',
      input:
        'shadow-inputShadow body flex h-auto items-center rounded-2xl px-4 py-3 placeholder-flexcar-neutral50 outline-none transition-colors hover:bg-flexcar-neutral95 hover:text-accent-foreground focus:ring-flexcar-blue40 focus:shadow-none ',
      // TODO: refactor outline into its own component - should be a part of dropdown
      outline:
        'h-10 px-4 py-2 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ring ring-input bg-background hover:bg-accent hover:text-accent-foreground',
      homepageGradient:
        'h-auto flex items-center justify-center rounded-[40px] bg-[linear-gradient(270deg,_#25BCB6_0%,_#3A00E5_100%)] px-[48px] py-[12px] hover:shadow-shadow30 active:shadow-shadow30 hover:cursor-pointer',
      homepage: 'font-sans text-base font-[500] leading-[24px] hover:cursor-pointer',
    },
    size: {
      big: 'h-14',
      small: 'h-12',
      none: '',
    },
    buttonDisabled: {
      true: '',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'big',
    buttonDisabled: false,
  },
  compoundVariants: [
    {
      variant: 'primary',
      size: ['big', 'small'],
      buttonDisabled: false,
      class:
        'active:shadow-none shadow-buttonShadow flex justify-center items-center flex-shrink-0 body-strong transition-colors rounded-2xl px-3 py-6 bg-flexcar-neutral1 ring-flexcar-neutral1 ring-2 text-flexcar-neutral100 active:ring-flexcar-neutral50 active:ring-2 active:px-[10px] active:py-[22px] active:bg-flexcar-neutral1 hover:bg-flexcar-neutral20 hover:ring-flexcar-neutral20 focus:ring-flexcar-blue40 focus:ring-4 focus:outline-none focus:shadow-none',
    },
    {
      variant: 'primary',
      size: ['big', 'small'],
      buttonDisabled: true,
      class:
        'flex justify-center items-center flex-shrink-0 body-strong transition-colors rounded-2xl cursor-default px-3 py-6 bg-flexcar-neutral80 text-flexcar-neutral60 placeholder-flexcar-neutral70 pointer-events-none',
    },
    {
      variant: 'secondary',
      size: ['big', 'small'],
      buttonDisabled: false,
      class:
        'active:shadow-none shadow-buttonShadow flex justify-center items-center flex-shrink-0 body-strong transition-colors rounded-2xl px-[10px] py-[22px] ring-2 bg-flexcar-neutral100 ring-flexcar-neutral80 text-flexcar-neutral1 hover:text-flexcar-blue40 active:text-flexcar-blue40 focus:ring-flexcar-blue40 focus:ring-4 focus:outline-none focus:shadow-none',
    },
    {
      variant: 'secondary',
      size: ['big', 'small'],
      buttonDisabled: true,
      class:
        'flex justify-center items-center flex-shrink-0 body-strong transition-colors rounded-2xl cursor-default px-[10px] py-[22px] ring-2 bg-flexcar-neutral100 ring-flexcar-neutral80 text-flexcar-neutral70 pointer-events-none',
    },
    {
      variant: 'destructive',
      size: ['big', 'small'],
      buttonDisabled: false,
      class:
        'active:shadow-none shadow-buttonShadow flex justify-center items-center flex-shrink-0 body-strong transition-colors rounded-2xl px-[10px] py-[22px] ring-2 bg-flexcar-neutral100 ring-flexcar-neutral80 text-flexcar-red50 hover:text-flexcar-red40 active:text-flexcar-red50 focus:ring-flexcar-red50 focus:ring-4 focus:outline-none focus:shadow-none',
    },
    {
      variant: 'destructive',
      size: ['big', 'small'],
      buttonDisabled: true,
      class:
        'flex justify-center items-center flex-shrink-0 body-strong transition-colors rounded-2xl cursor-default px-[10px] py-[22px] ring-2 bg-flexcar-neutral100 ring-flexcar-neutral80 text-flexcar-neutral70 pointer-events-none',
    },
    {
      variant: 'tertiary',
      size: ['big', 'small'],
      buttonDisabled: false,
      class:
        'focus:ring-none body-strong m-0 h-auto whitespace-normal p-0 text-flexcar-blue30 transition-colors hover:text-flexcar-blue40',
    },
    {
      variant: 'tertiary',
      size: ['big', 'small'],
      buttonDisabled: true,
      class:
        'p-0 m-0 whitespace-normal focus:ring-none body-strong h-auto text-flexcar-neutral60 transition-colors hover:text-flexcar-neutral60 cursor-default pointer-events-none',
    },
    {
      variant: 'input',
      size: ['none'],
      buttonDisabled: true,
      class: 'bg-flexcar-neutral90 pointer-events-none',
    },
    {
      variant: 'input',
      size: ['none'],
      buttonDisabled: false,
      class: 'ring-2 ring-flexcar-neutral80',
    },
    {
      variant: 'clear',
      size: ['big', 'small', 'none'],
      buttonDisabled: false,
      class: 'p-0 m-0 body whitespace-normal focus:ring-none',
    },
    {
      variant: 'clear',
      size: ['big', 'small', 'none'],
      buttonDisabled: true,
      class:
        'p-0 m-0 body whitespace-normal focus:ring-none pointer-events-none opacity-50',
    },
  ],
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, 'buttonDisabled'> {
  loading?: boolean;
  retainContentWhileLoading?: boolean;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      disabled,
      children,
      retainContentWhileLoading,
      loading = false,
      asChild = false,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const disabledStateOverride = loading || disabled;
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            className,
            buttonDisabled: disabledStateOverride,
          })
        )}
        ref={ref}
        {...props}
      >
        {loading && retainContentWhileLoading ? (
          <>
            <Spinner width={32} height={32} />
            {children}
          </>
        ) : loading ? (
          <Spinner />
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
